import { GeometryType, ProjectContentItemDc } from "@evergis/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeProps } from "find-and";
import { debounce } from "@evergis/sgis/es/utils/utils";
import { TreeDestinationPosition, TreeItem, TreeSourcePosition } from "@atlaskit/tree";
import { getTempSourceLayers } from "../../../ducks/tempSourceLayers/selectors";

import { generateTree } from "../../../utils/layers";
import { reoderTree } from "../../../utils/array";
import { layersTreeFilter } from "./utils";
import { setGroupExpanded } from "../../../ducks/sharedLayers/sharedLayers";
import { getGroupsExpanded } from "../../../ducks/sharedLayers/selectors";
import { useProjectUpdate } from "../../../ducks/project/hooks";
import { combineLayers, useProjectLayerInfos, useProjectLayers } from "../useProjectLayers";
import { useWidgetConfiguration } from "../../../modules/Dashboard/hooks/useWidgetConfiguration";
import { useIsShared } from "../../api/useIsShared";
import { WidgetType } from "../../../modules/FeatureCard/types";

const DEBOUNCE_DELAY = 250;

// eslint-disable-next-line max-lines-per-function
export function useLayersTree({
  layerNames,
  searchFilter = "",
  noGroups = false,
  filterByConfig = false,
  hasGeometry = false,
}: {
  layerNames?: string[];
  searchFilter?: string;
  noGroups?: boolean;
  filterByConfig?: boolean;
  hasGeometry?: boolean;
}) {
  const isShared = useIsShared();
  const dispatch = useDispatch();
  const mutation = useProjectUpdate();
  const layers = useProjectLayers();
  const groupsExpanded = useSelector(getGroupsExpanded);
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const { layerInfos } = useProjectLayerInfos();
  const { currentConfig } = useWidgetConfiguration(WidgetType.Dashboard);

  const allLayers = useMemo((): ProjectContentItemDc[] => {
    const sourceLayers = layerNames?.length ? layers.filter(({ name }) => layerNames.includes(name)) : layers;
    const formattedLayers = noGroups ? combineLayers(sourceLayers, { onlyVisible: false }) : sourceLayers;

    return [
      ...tempSourceLayers.map(({ name }) => ({ name, isVisible: true, opacity: 1 })),
      ...(filterByConfig
        ? formattedLayers.filter(layer => currentConfig.layers?.some(({ name }) => name === layer.name))
        : formattedLayers),
    ];
  }, [currentConfig.layers, filterByConfig, layerNames, layers, noGroups, tempSourceLayers]);

  const [tree, setTree] = useState(generateTree(allLayers, groupsExpanded));

  const filteredLayers = useMemo(() => {
    let result = [...allLayers];

    if (hasGeometry) {
      result = result.filter(({ name, children }) => {
        if (children?.length) return true;

        const layer = layerInfos.find(layerInfo => layerInfo.name === name);

        return layer && layer.geometryType !== GeometryType.Unknown;
      });
    }

    result = layersTreeFilter(result, searchFilter);

    return result;
  }, [allLayers, hasGeometry, layerInfos, searchFilter]);

  const onTreeDragEnd = useCallback(
    (source: TreeSourcePosition, destination: TreeDestinationPosition) => {
      if (source && destination) {
        const newLayers = reoderTree(filteredLayers, source, destination);

        setTree(generateTree(newLayers, groupsExpanded, searchFilter));

        mutation({
          content: {
            items: newLayers.filter(({ name }) => !name?.startsWith("temp")),
          },
        });
      }
    },
    [filteredLayers, groupsExpanded, mutation, searchFilter],
  );

  const onTreeExpand = debounce((item: TreeItem) => {
    if (!filteredLayers) {
      return;
    }

    const { isExpanded, data } = item;
    const newLayers = changeProps(filteredLayers, { name: data.name }, { isExpanded: !isExpanded });

    setTree(generateTree(newLayers, groupsExpanded, searchFilter));

    if (isShared) {
      dispatch(setGroupExpanded({ groupName: data.name, isExpanded: !isExpanded }));
    } else {
      mutation({
        content: {
          items: newLayers.filter(({ name }) => !name?.startsWith("temp")),
        },
      });
    }
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    setTree(generateTree(filteredLayers, groupsExpanded, searchFilter));
  }, [filteredLayers, groupsExpanded, searchFilter]);

  return {
    tree,
    onTreeExpand,
    onTreeDragEnd,
  };
}
